import { type FC, useState } from 'react';

import {
  Button,
  Checkbox,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { IconOutlineHelpCircle } from '@/components';
import { usePageAdminInterviewContext } from '@/features/admin-interview/PageAdminInterview.context';
import { type AdminQuestionDto, type CandidateBasicDto } from '@/services/api';

import { useReInterviewMutation } from './useCandidateTableMutation';

type ReInterviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  candidate: CandidateBasicDto;
  interviewId: string;
};

export const ReInterviewModal: FC<ReInterviewModalProps> = ({
  isOpen,
  onClose,
  candidate,
  interviewId,
}) => {
  const { t } = useTranslation(['common', 'interview']);
  const { questionsData } = usePageAdminInterviewContext();
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(
    new Set()
  );
  const [showAlert, setShowAlert] = useState(false);

  const reInterviewMutation = useReInterviewMutation({
    onSuccess: () => {
      onClose();
    },
  });

  const questions = questionsData?.questions ?? [];

  const handleQuestionToggle = (questionId: string) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedQuestions(newSelected);
    setShowAlert(false);
  };

  const handleSendInterview = () => {
    if (selectedQuestions.size === 0) {
      setShowAlert(true);
      return;
    }

    reInterviewMutation.mutate({
      interviewsId: interviewId,
      candidateId: candidate.id,
      body: {
        questionIds: Array.from(selectedQuestions),
      },
    });
  };

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="blackAlpha.200" />
      <ModalContent maxWidth="624px" borderRadius="16px" p={0}>
        <ModalCloseButton
          borderRadius="full"
          borderWidth={1}
          borderColor="gray.50"
          mt={3}
          mr={3}
          bg="white"
          size="lg"
        />

        <ModalBody p="32px 24px 24px">
          <VStack gap={4} align="stretch">
            {/* Title */}
            <Text
              fontWeight="semibold"
              color="gray.900"
              fontSize="20px"
              textAlign="center"
              letterSpacing="-2%"
              lineHeight="1.4em"
            >
              {t('interview:reInterview.title')}
            </Text>

            {/* Alert Message */}
            {showAlert && (
              <Flex
                bg="#F7FBFF"
                border="1px solid #E9F1FA"
                borderRadius="12px"
                p={2}
                gap={4}
                align="center"
                justify="space-between"
              >
                <HStack gap={3}>
                  <Flex
                    bg="#E9F1FA"
                    borderRadius="36px"
                    w="32px"
                    h="32px"
                    align="center"
                    justify="center"
                  >
                    <IconOutlineHelpCircle fontSize={20} color="#2370D1" />
                  </Flex>
                  <Text
                    color="gray.900"
                    fontSize="14px"
                    fontWeight="medium"
                    letterSpacing="-1%"
                    lineHeight="1.43em"
                  >
                    {t('interview:reInterview.alertMessage')}
                  </Text>
                </HStack>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAlert(false)}
                  p={1}
                >
                  <IconOutlineHelpCircle fontSize={20} color="#6F7881" />
                </Button>
              </Flex>
            )}

            {/* Questions Container */}
            <VStack
              bg="#FAFBFB"
              borderRadius="16px"
              p={2}
              gap={2}
              align="stretch"
            >
              {questions.map((question: AdminQuestionDto, index: number) => (
                <Flex
                  key={question.id}
                  bg="white"
                  border="1px solid #E9EAED"
                  borderRadius="12px"
                  p={3}
                  gap={3}
                  align="stretch"
                >
                  <VStack gap={2} align="stretch" flex={1}>
                    <HStack gap={2} align="center">
                      <Checkbox
                        isChecked={selectedQuestions.has(question.id)}
                        onChange={() => handleQuestionToggle(question.id)}
                        colorScheme="purple"
                        size="md"
                        borderRadius="6px"
                        borderColor="#E9EAED"
                        _checked={{
                          bg: '#6421B9',
                          borderColor: '#6421B9',
                        }}
                      />
                      <Text
                        color="#6F7881"
                        fontSize="14px"
                        fontWeight="normal"
                        letterSpacing="-1%"
                        lineHeight="1.43em"
                      >
                        {index + 1}.Soru
                      </Text>
                    </HStack>
                    <Text
                      color="gray.900"
                      fontSize="16px"
                      fontWeight="normal"
                      letterSpacing="-2%"
                      lineHeight="1.5em"
                      pl={8}
                    >
                      {question.question}
                    </Text>
                  </VStack>
                </Flex>
              ))}
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter p="24px">
          <Button
            bg="#6421B9"
            color="white"
            borderRadius="12px"
            px={4}
            py={3}
            fontSize="16px"
            fontWeight="semibold"
            letterSpacing="-2%"
            lineHeight="1.5em"
            onClick={handleSendInterview}
            isLoading={reInterviewMutation.isPending}
            loadingText={t('interview:reInterview.sendInterview')}
            _hover={{
              bg: '#5a1ea0',
            }}
            _active={{
              bg: '#4f1b8a',
            }}
          >
            {t('interview:reInterview.sendInterview')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
